/**
 * Commission vocabulary, routes and the words that decide when somebody gets paid.
 *
 * `STATUS_MEANING` is not decoration. "Pending", "Approved" and "Paid" determine when money
 * reaches an officer, and an officer who cannot tell them apart opens a support ticket
 * (frontend phase-10 §2.1). The explanations live beside the labels so the two cannot drift.
 */

export const ROUTES = {
  /** The officer's own screens. */
  commissions: '/app/affiliate/commissions',
  payouts: '/app/affiliate/commissions/payouts',
  entry: (id: string) => `/app/affiliate/commissions/${id}`,

  /** The administrator's console. */
  adminLedger: '/app/admin/commissions',
  adminApprove: '/app/admin/commissions/approve',
  adminPayouts: '/app/admin/commissions/payouts',
  adminUnmatched: '/app/admin/commissions/unmatched',
  adminRules: '/app/admin/commission-rules',
  adminRuleNew: '/app/admin/commission-rules/new',
  adminRule: (id: string) => `/app/admin/commission-rules/${id}`,
} as const

/** Read-only for the officer, and enforced server-side regardless. */
export const CAPABILITIES = {
  commissionView: 'affiliate.commission.view',
  adminAccess: 'admin.access',
} as const

export const ENTRY_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Paid', value: 'paid' },
  { label: 'Reversed', value: 'reversed' },
  { label: 'Cancelled', value: 'cancelled' },
]

export const ENTRY_KIND_OPTIONS = [
  { label: 'Earnings', value: 'earning' },
  { label: 'Reversals', value: 'reversal' },
]

export const PAYOUT_STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Paid', value: 'paid' },
  { label: 'Cancelled', value: 'cancelled' },
]

export const SCOPE_OPTIONS = [
  { label: 'All affiliates', value: 'global' },
  { label: 'One affiliate', value: 'affiliate' },
  { label: 'One landlord', value: 'landlord' },
  { label: 'One property', value: 'property' },
]

export const BASIS_OPTIONS = [
  { label: 'Base rent (excl. service charges)', value: 'base_rent' },
  { label: 'Gross amount (incl. service charges)', value: 'gross' },
  { label: 'Settlement amount (net of gateway fees)', value: 'settlement' },
]

/** What each status actually means for the officer's bank account. */
export const STATUS_MEANING: Record<string, string> = {
  pending: 'Earned and recorded. An administrator has not reviewed it yet.',
  approved: 'Reviewed and cleared for payment. It will be in a payout.',
  paid: 'Included in a payout that has been settled.',
  reversed: 'Cancelled by a refund or a correction. The original entry is kept, not deleted.',
  cancelled: 'Withdrawn before it was ever approved.',
}

export const UNMATCHED_REASON_HELP: Record<string, string> = {
  no_attribution:
    'No officer was attributed to this property or its landlord when the payment settled. Attribute the property, then retry.',
  no_rule:
    'No commission rule covered this officer, landlord or property on that date. Create a rule, then retry.',
  affiliate_terminated:
    'The officer was terminated before this payment settled. Commission accrues up to the termination date only.',
  excluded_offline:
    'The rule excludes payments the landlord recorded offline. Enable offline payments on the rule if that is wrong.',
  excluded_renewal: 'The rule excludes renewals.',
  excluded_not_first: 'The rule pays on the first payment of a tenancy only.',
}

/**
 * Said plainly on the payout screens.
 *
 * APMP records disbursement; it does not perform it. An officer who reads "Paid" and expects
 * the platform to have moved money has been misled by the software.
 */
export const PAYOUT_DISCLAIMER =
  'APMP records payouts; it does not transfer the money. Each payout names the reference of a transfer made outside the platform.'

/** The largest batch the server will approve in one request. Mirrors `MAX_APPROVAL_BATCH`. */
export const MAX_APPROVAL_BATCH = 500
